import React from 'react';
import { useField } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Option } from '../../interfaces';
import { cx } from '../../helpers/global';

interface Props {
  name: string;
  label: string;
  options: Option<string>[];
  placeholder: string | undefined;
  disabled: boolean;
  hidden?: boolean;
  tooltip?: string;
  allText?: string;
  autoWidth?: boolean;
  showListWithAll?: boolean;
  hideAllOption?: boolean;
}

export default function MultiSelectInput({
  name,
  label,
  options,
  placeholder,
  disabled,
  hidden,
  tooltip,
  allText,
  autoWidth = true,
  showListWithAll,
  hideAllOption,
}: Props) {
  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);
  const isError = touched && !!error;

  const isSmall = useMediaQuery('(max-width: 900px)');
  const selectedList = (value ?? []) as string[];

  const isAllSelected = options.every(option =>
    selectedList.includes(option.value),
  );

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return placeholder || `No ${name}`;
    }
    if (isAllSelected && !showListWithAll) {
      return allText || `All ${name}`;
    }
    const selectedLabels = selected.map(
      id => options.find(option => option.value === id)?.label,
    );
    return selectedLabels.join(', ');
  };

  const onBlur = React.useCallback(() => {
    setTouched(true);
  }, [setTouched]);

  return (
    <div className={cx('input', hidden && 'hidden', isError && 'error')}>
      <InputLabel
        htmlFor={name}
        sx={{
          display: 'flex',
          justifyContent: isSmall ? 'flex-start' : 'space-between',
          alignItems: 'center',
          gap: 1,
        }}>
        <Typography variant="h5">{label}</Typography>
        {!!tooltip && (
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            enterTouchDelay={0}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -5],
                    },
                  },
                ],
              },
            }}>
            <Box
              component="span"
              sx={{
                my: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}>
              <Button
                type="button"
                variant="text"
                color="inherit"
                disableRipple
                sx={{
                  width: 'auto',
                  minWidth: 0,
                  padding: 0,
                  ':hover': {
                    backgroundColor: 'transparent',
                  },
                }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: isSmall ? 16 : 12 }}
                  fontSize="small"
                />
              </Button>
            </Box>
          </Tooltip>
        )}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={async (event: SelectChangeEvent) => {
          const { value: newValue } = event.target;
          const newList = newValue as unknown as string[];
          if (newList.includes('all') && !isAllSelected) {
            setValue(options.map(option => option.value));
            return;
          }
          if (newList.includes('all') && isAllSelected) {
            setValue([]);
            return;
          }
          setValue(newList, true);
          // Fix bug where validation was re-ran with previous value
          setTimeout(() => setTouched(true), 0);
        }}
        input={<OutlinedInput />}
        autoWidth={autoWidth}
        displayEmpty
        label={placeholder}
        disabled={disabled}
        onBlur={onBlur}
        renderValue={selected => renderValue(selected as unknown as string[])}>
        {!hideAllOption && (
          <MenuItem key="all" value="all" sx={{ gap: 1 }}>
            <Checkbox sx={{ padding: 0.5 }} checked={isAllSelected} />
            <ListItemText
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
              primary={allText || `All ${name}`}
            />
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem
            key={option.label + option.value}
            value={option.value}
            sx={{ gap: 1 }}>
            <Checkbox
              checked={selectedList.includes(option.value)}
              sx={{
                padding: 0.5,
              }}
            />
            <ListItemText
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
              primary={option.label}
            />
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}
